export interface AddressComponents {
  street: string;
  city: string;
  state: string;
  postalCode: string;
  unit?: string;
}

export interface AddressSuggestion {
  description: string;
  placeId?: string;
  addressToken?: string;
  source: 'google' | 'casa';
  /** Structured components, present when Casa autocomplete resolved the address. */
  components?: AddressComponents;
}

interface GooglePrediction {
  description: string;
  place_id?: string;
}

interface CasaPrediction {
  description?: string;
  formattedAddress?: string;
  addressToken: string;
  street?: string;
  unitInfo?: string;
  city?: string;
  state?: string;
  postalCode?: string;
}

type RawPrediction = GooglePrediction | CasaPrediction;

interface AutocompleteResponse {
  predictions?: RawPrediction[];
}

function isCasaPrediction(p: RawPrediction): p is CasaPrediction {
  return 'addressToken' in p && typeof p.addressToken === 'string';
}

function normalize(p: RawPrediction): AddressSuggestion {
  if (isCasaPrediction(p)) {
    const components =
      p.street && p.city && p.state && p.postalCode
        ? {
            street: p.street,
            city: p.city,
            state: p.state,
            postalCode: p.postalCode,
            ...(p.unitInfo ? { unit: p.unitInfo } : {}),
          }
        : undefined;
    return {
      description: p.description ?? p.formattedAddress ?? '',
      addressToken: p.addressToken,
      source: 'casa',
      components,
    };
  }
  return {
    description: p.description ?? '',
    placeId: p.place_id,
    source: 'google',
  };
}

interface PlaceResponse {
  street1?: string;
  unit?: string;
  city?: string;
  state?: string;
  postal_code?: string;
}

// Resolves a suggestion to structured components via the /place endpoint when
// not already inline, mirroring the production homepage. Returns null if unresolved.
export async function resolveAddressComponents(
  suggestion: AddressSuggestion,
  sessionToken: string,
): Promise<AddressComponents | null> {
  if (suggestion.components) return suggestion.components;

  const params = new URLSearchParams();
  if (suggestion.addressToken) {
    params.set('addressToken', suggestion.addressToken);
  } else if (suggestion.placeId) {
    params.set('placeId', suggestion.placeId);
    params.set('sessionToken', sessionToken);
  } else {
    return null;
  }

  let data: PlaceResponse;
  try {
    const res = await fetch(`/_address-input/place?${params.toString()}`);
    if (!res.ok) return null;
    data = (await res.json()) as PlaceResponse;
  } catch {
    return null;
  }

  if (!data.street1 || !data.city || !data.state || !data.postal_code) return null;
  return {
    street: data.street1,
    city: data.city,
    state: data.state,
    postalCode: data.postal_code,
    ...(data.unit ? { unit: data.unit } : {}),
  };
}

export async function fetchAddressSuggestions(
  query: string,
  sessionToken: string,
  signal?: AbortSignal,
): Promise<AddressSuggestion[]> {
  if (!query.trim()) return [];
  const url = `/_address-input/autocomplete?sessionToken=${encodeURIComponent(
    sessionToken,
  )}&query=${encodeURIComponent(query)}`;
  const res = await fetch(url, { signal });
  if (!res.ok) {
    throw new Error(`Address autocomplete failed (${res.status})`);
  }
  let data: AutocompleteResponse;
  try {
    data = (await res.json()) as AutocompleteResponse;
  } catch {
    return [];
  }
  return (data.predictions ?? []).map(normalize);
}
